/** @jsxImportSource @emotion/react */
import { memo, useState } from "react";
import { EuiButtonIcon, EuiIcon, EuiPopover } from "@inscopix/ideas-eui";
import { css } from "@emotion/react";
import { useMouseOver } from "hooks/useMouseOver";
import { ColumnHeaderActionRemoveColumn } from "./actions/ColumnHeaderActionRemoveColumn";
import { ColumnHeaderBaseName } from "./ColumnHeaderBaseName";
import { useDataTableContext } from "../store/DataTableProvider";
import { ModalDeleteColumn } from "../modals/ModelDeleteColumn";
import {
  DataTableColumnDefinition,
  DataTableColumnGroupDefinition,
} from "../store/DataTableProvider.types";
import { ModalEditRowIdentifierFormat } from "../modals/ModalEditRowIdentifierFormat";
import { ModalEditColumn } from "../modals/ModalEditColumn";
import { ColumnHeaderBaseMetadatumLink } from "./ColumnHeaderBaseMetadatumLink";
import { ButtonIconPermissioned } from "components/ButtonIconPermissioned/ButtonIconPermissioned";
import { ButtonEmptyPermissioned } from "components/ButtonEmptyPermissioned/ButtonEmptyPermissioned";
import { useProjectPermissionStore } from "pages/project/ProjectPermissionProvider";
import { DataTableRowData } from "../layout/DataTable";
import { GridApi } from "ag-grid-community";
import { Tooltip } from "components/Tooltip/Tooltip";
import { ModalFillSource } from "../modals/ModalCreateColumn/ModalFillSource";

/**
 * Gets the spreadsheet column address for column a specified position.
 * @param order
 * @returns The spreadsheet column address
 */
export const getColumnAddress = (order: number) => {
  let address = "";

  // Convert order to a base-64 number where A is 0 and Z is 25. Every loop we
  // reduce order by a power of 26 and calculate the character corresponding to
  // that power. Every character is offset from char code 65, which represents A.
  while (order >= 0) {
    const remainder = order % 26;
    address = String.fromCharCode(65 + remainder) + address;
    order = Math.floor(order / 26) - 1;
  }

  return address;
};

const styles = {
  root: css`
    align-items: center;
    display: flex;
    height: 100%;
    width: 100%;
  `,
  address: css`
    color: grey;
    margin-left: 5px;
  `,
  contextMenu: css`
    align-items: flex-start;
    display: flex;
    flex-direction: column;
  `,
};

interface ColumnHeaderBaseProps {
  displayName: string;
  eGridHeader: HTMLElement;
  index: number;
  tableId: string;
  tableKind: "data" | "analysis";
  columnId: string;
  columnDefinition: DataTableColumnDefinition;
  columnGroupDefinition: DataTableColumnGroupDefinition | undefined;
  isColumnDeletable: boolean;
  isPinned: boolean;
  helpText: string | null;
  isIdColumn: boolean;
  defaultFormula: string | null;
  suppressMenu?: boolean;
  gridApi: GridApi<DataTableRowData>;
}

/**
 * Component that renders a header for all column types.
 *
 * All all other header components should render this header and specify props
 * for displaying custom content.
 */
const ColumnHeaderBase = ({
  displayName,
  eGridHeader,
  index,
  tableId,
  tableKind,
  columnId,
  columnDefinition,
  columnGroupDefinition,
  isColumnDeletable,
  isPinned,
  helpText,
  isIdColumn,
  defaultFormula,
  suppressMenu,
  gridApi,
}: ColumnHeaderBaseProps) => {
  const isEditingPermitted = useProjectPermissionStore(
    (s) => s.permissions.edit,
  );
  const isAdvancedMode = useDataTableContext((s) => s.isAdvancedMode);
  const pinColumn = useDataTableContext((s) => s.pinColumn);
  const unpinColumn = useDataTableContext((s) => s.unpinColumn);
  const { isMouseOver } = useMouseOver(eGridHeader);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const togglePopover = () => setIsPopoverOpen((isOpen) => !isOpen);
  const closePopover = () => setIsPopoverOpen(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [visibleModal, setVisibleModal] = useState<
    "details" | "identifier" | "remove" | "fillSource"
  >();

  return (
    <div css={styles.root} title={helpText ?? undefined}>
      {visibleModal === "fillSource" && (
        <ModalFillSource
          tableId={tableId}
          onClose={() => setVisibleModal(undefined)}
          sourceColId={columnId}
        />
      )}
      {visibleModal === "details" && (
        <ModalEditColumn
          tableId={tableId}
          id={columnId}
          name={displayName}
          helpText={helpText}
          isDefinitionEditable={tableKind === "data" && isEditingPermitted}
          definition={columnDefinition}
          groupDefinition={columnGroupDefinition}
          defaultFormula={defaultFormula}
          isIdColumn={isIdColumn}
          onClose={() => setVisibleModal(undefined)}
        />
      )}

      {visibleModal === "identifier" && (
        <ModalEditRowIdentifierFormat
          tableId={tableId}
          onClose={() => setVisibleModal(undefined)}
        />
      )}

      {visibleModal === "remove" && (
        <ModalDeleteColumn
          tableId={tableId}
          columnId={columnId}
          onClose={() => setVisibleModal(undefined)}
        />
      )}

      <ColumnHeaderBaseName
        tableId={tableId}
        columnId={columnId}
        isRenaming={isRenaming}
        setIsRenaming={setIsRenaming}
        name={displayName}
      />

      {isPinned && (
        <EuiIcon
          aria-label="Pinned"
          color="subdued"
          size="s"
          type="pinFilled"
        />
      )}

      {isAdvancedMode && (
        <span css={styles.address}>{getColumnAddress(index)}</span>
      )}

      {(isMouseOver || isPopoverOpen) && !suppressMenu && (
        <EuiPopover
          button={
            <EuiButtonIcon
              aria-label="Context menu"
              color="text"
              iconType={() => (
                <span className="ag-icon ag-icon-menu" unselectable="on" />
              )}
              size="xs"
              onClick={togglePopover}
            />
          }
          isOpen={isPopoverOpen}
          closePopover={closePopover}
          anchorPosition="downLeft"
          panelPaddingSize="none"
          ownFocus={false}
        >
          <div css={styles.contextMenu}>
            <ButtonEmptyPermissioned
              requiredPermission="view"
              onClick={() => {
                setVisibleModal("details");
                closePopover();
              }}
              iconType="pencil"
              color="text"
              size="s"
            >
              Edit
            </ButtonEmptyPermissioned>

            <ButtonEmptyPermissioned
              requiredPermission="edit"
              onClick={() => {
                closePopover();
                setIsRenaming(true);
              }}
              iconType="visText"
              color="text"
              size="s"
            >
              Rename column
            </ButtonEmptyPermissioned>

            <ButtonEmptyPermissioned
              requiredPermission="edit"
              onClick={() => {
                closePopover();
                if (isPinned) {
                  void unpinColumn({
                    tableId,
                    columnId,
                  });
                } else {
                  void pinColumn({
                    tableId,
                    columnId,
                  });
                }
              }}
              iconType="pin"
              color="text"
              size="s"
            >
              {isPinned ? "Unpin column" : "Pin column"}
            </ButtonEmptyPermissioned>

            {tableKind === "data" && (
              <ColumnHeaderActionRemoveColumn
                onClick={() => {
                  setVisibleModal("remove");
                  closePopover();
                }}
                isDisabled={!isColumnDeletable}
              />
            )}
          </div>
        </EuiPopover>
      )}
      {columnDefinition.kind === "identifier" &&
        columnGroupDefinition?.kind === "sources" &&
        !suppressMenu && (
          <Tooltip content="Fill Sources">
            <ButtonIconPermissioned
              requiredPermission="edit"
              aria-label="Fill Sources"
              size="xs"
              iconType="sparkles"
              onClick={() => setVisibleModal("fillSource")}
            />
          </Tooltip>
        )}

      {columnDefinition.kind === "metadatum" && (
        <ColumnHeaderBaseMetadatumLink
          columnDefinition={columnDefinition}
          gridApi={gridApi}
        />
      )}

      {isIdColumn && !suppressMenu && (
        <ButtonIconPermissioned
          requiredPermission="edit"
          aria-label="Edit row identifier format"
          iconSize="s"
          iconType="pencil"
          size="xs"
          onClick={() => setVisibleModal("identifier")}
        />
      )}
    </div>
  );
};

export const ColumnHeaderBaseMemo = memo(ColumnHeaderBase);
